import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, RefreshCw, Wand2, ScrollText, GitPullRequest, AlertTriangle,
  Plus, X, Check, ChevronDown, ChevronRight, ShieldCheck, ClipboardCheck,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { piApi, releaseApi, workItemApi } from '../services/api';
import { useProjectStore } from '../store/useProjectStore';
import { usePIStore } from '../store/usePIStore';
import { useOrgHierarchyStore } from '../store/useOrgHierarchyStore';
import type { Release, WorkItem } from '../types';

/** Only these types may be assigned to a release — the API enforces it too. */
const RELEASABLE = ['feature', 'bug', 'security'] as const;

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  planned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  released: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  closed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export default function ReleasesPage() {
  const navigate = useNavigate();
  const { projects } = useProjectStore();
  const { pis, sprints, fetchPIs, fetchAllSprints } = usePIStore();
  const { releaseTrains, fetchReleaseTrains } = useOrgHierarchyStore();

  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    releaseTrainId: '', piId: '', sprintId: '', projectId: '',
    status: '', releaseType: '', includeClosed: false,
  });

  useEffect(() => {
    fetchPIs();
    fetchAllSprints();
    fetchReleaseTrains();
  }, [fetchPIs, fetchAllSprints, fetchReleaseTrains]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReleases(await releaseApi.list(filters));
    } catch (e: any) {
      toast.error(e?.message || 'Could not load releases');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Cascade the hierarchy filters so the options can't contradict each other.
  const visiblePis = useMemo(
    () => (filters.releaseTrainId
      ? pis.filter((p) => p.releaseTrainId === filters.releaseTrainId)
      : pis),
    [pis, filters.releaseTrainId],
  );
  const visibleSprints = useMemo(
    () => (filters.piId ? sprints.filter((s) => s.piId === filters.piId) : sprints),
    [sprints, filters.piId],
  );
  const visibleProjects = useMemo(() => {
    if (filters.piId) {
      const pi = pis.find((p) => p._id === filters.piId);
      const ids = pi?.projectIds || [];
      if (ids.length) return projects.filter((p) => ids.includes(p._id));
    }
    if (filters.releaseTrainId) {
      return projects.filter((p) => p.releaseTrainId === filters.releaseTrainId);
    }
    return projects;
  }, [projects, pis, filters.piId, filters.releaseTrainId]);

  const set = (key: keyof typeof filters, value: string | boolean) =>
    setFilters((f) => {
      const next = { ...f, [key]: value };
      // Clear anything downstream that the new choice invalidates.
      if (key === 'releaseTrainId') { next.piId = ''; next.sprintId = ''; next.projectId = ''; }
      if (key === 'piId') { next.sprintId = ''; }
      return next;
    });

  const nameOf = (id?: string, list: { _id: string; name: string }[] = []) =>
    list.find((x) => x._id === id)?.name || '';

  const generate = async () => {
    if (!filters.piId) {
      toast.error('Pick a Program Increment to generate releases for');
      return;
    }
    try {
      const r = await piApi.generateReleases(filters.piId);
      toast.success(
        r.created
          ? `Created ${r.created} release${r.created === 1 ? '' : 's'}` +
            (r.skipped ? `, ${r.skipped} already existed` : '')
          : `Nothing to create — all ${r.skipped} already exist`,
      );
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Generation failed');
    }
  };

  return (
    <div className="px-8 py-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Package size={20} className="text-accent" />
            Releases
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            One release per project per sprint. Only features, bugs and security
            items can ship in a release.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generate}
            disabled={!filters.piId}
            title={filters.piId
              ? 'Create any missing releases for this PI'
              : 'Pick a Program Increment first'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
          >
            <Wand2 size={14} />
            Generate for PI
          </button>
          <button
            onClick={load}
            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <Select label="Release train" value={filters.releaseTrainId}
                onChange={(v) => set('releaseTrainId', v)}
                options={releaseTrains.map((t) => ({ id: t._id, name: t.name }))} />
        <Select label="Program Increment" value={filters.piId}
                onChange={(v) => set('piId', v)}
                options={visiblePis.map((p) => ({ id: p._id, name: p.name }))} />
        <Select label="Sprint" value={filters.sprintId}
                onChange={(v) => set('sprintId', v)}
                options={visibleSprints.map((s) => ({ id: s._id, name: s.name }))} />
        <Select label="Project" value={filters.projectId}
                onChange={(v) => set('projectId', v)}
                options={visibleProjects.map((p) => ({ id: p._id, name: p.name }))} />
        <Select label="Status" value={filters.status}
                onChange={(v) => set('status', v)}
                options={['draft', 'planned', 'released', 'cancelled', 'closed']
                  .map((s) => ({ id: s, name: s }))} />
        <Select label="Type" value={filters.releaseType}
                onChange={(v) => set('releaseType', v)}
                options={[{ id: 'scheduled', name: 'scheduled' },
                          { id: 'standalone', name: 'off-cycle' }]} />
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 pb-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.includeClosed}
            onChange={(e) => set('includeClosed', e.target.checked)}
            className="rounded border-gray-300 text-accent focus:ring-accent"
          />
          Show empty/closed
        </label>
      </div>

      {/* List */}
      {releases.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
          <Package size={32} className="mb-2" />
          <p className="text-sm">No releases match these filters.</p>
          {filters.piId && (
            <button onClick={generate} className="mt-3 text-xs text-accent hover:underline">
              Generate releases for this PI
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          {releases.map((rel) => (
            <ReleaseRow
              key={rel._id}
              release={rel}
              projectName={nameOf(rel.projectId, projects as any)}
              sprintName={nameOf(rel.sprintId, sprints as any)}
              expanded={expanded === rel._id}
              onToggle={() => setExpanded(expanded === rel._id ? null : rel._id)}
              onChanged={load}
              onOpenNotes={() => navigate(`/release-notes/${encodeURIComponent(rel._id)}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { id: string; name: string }[];
}) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 min-w-[8rem]"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}

function ReleaseRow({ release, projectName, sprintName, expanded, onToggle,
                     onChanged, onOpenNotes }: {
  release: Release; projectName: string; sprintName: string;
  expanded: boolean; onToggle: () => void; onChanged: () => void;
  onOpenNotes: () => void;
}) {
  const date = release.plannedDate || release.releaseDate;
  const count = (release.workItemIds || release.featureIds || []).length;
  const isEmptyClosed = release.status === 'closed'
    && release.closureReason === 'no_content';

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-white dark:bg-gray-800">
        <button onClick={onToggle} className="p-0.5 text-gray-400">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
          {release.version || '(no version)'}
        </span>
        {release.releaseType === 'standalone' && (
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            off-cycle
          </span>
        )}
        <span className={clsx('text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full',
                              STATUS_STYLES[release.status] || STATUS_STYLES.draft)}>
          {release.status}
        </span>
        {isEmptyClosed && (
          <span className="text-[10px] text-gray-400">nothing to report</span>
        )}
        <span className="flex-1" />
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[12rem]">
          {projectName}
        </span>
        <span className="text-xs text-gray-400 truncate max-w-[12rem] hidden md:inline">
          {sprintName}
        </span>
        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{date}</span>
        <span className="text-xs text-gray-400 w-14 text-right">
          {count} item{count === 1 ? '' : 's'}
        </span>
        <button
          onClick={onOpenNotes}
          className="p-1 rounded text-gray-400 hover:text-accent hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Release notes"
        >
          <ScrollText size={14} />
        </button>
      </div>

      {expanded && (
        <ReleaseDetail release={release} onChanged={onChanged} />
      )}
    </div>
  );
}

function ReleaseDetail({ release, onChanged }: {
  release: Release; onChanged: () => void;
}) {
  const [items, setItems] = useState<WorkItem[]>([]);
  const [candidates, setCandidates] = useState<WorkItem[]>([]);
  const [changes, setChanges] = useState<Awaited<ReturnType<typeof releaseApi.changes>> | null>(null);
  const [readiness, setReadiness] = useState<Awaited<ReturnType<typeof releaseApi.readiness>> | null>(null);
  const [lifecycle, setLifecycle] = useState<Awaited<ReturnType<typeof releaseApi.lifecycle>> | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forking, setForking] = useState(false);
  const [forkDate, setForkDate] = useState('');
  const [forkPicked, setForkPicked] = useState<string[]>([]);

  const memberIds = release.workItemIds || release.featureIds || [];

  const load = useCallback(async () => {
    const [all, ch, rd, lc] = await Promise.all([
      workItemApi.list({ projectId: release.projectId || '' }).catch(() => []),
      releaseApi.changes(release._id).catch(() => null),
      releaseApi.readiness(release._id).catch(() => null),
      releaseApi.lifecycle(release._id).catch(() => null),
    ]);
    const byId = new Map((all as WorkItem[]).map((w) => [w._id, w]));
    setItems(memberIds.map((id) => byId.get(id)).filter(Boolean) as WorkItem[]);
    setCandidates((all as WorkItem[]).filter(
      (w) => RELEASABLE.includes(w.workItemType as any)
        && !memberIds.includes(w._id)
        && !w.releaseId,
    ));
    setChanges(ch);
    setReadiness(rd);
    setLifecycle(lc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [release._id, release.projectId, memberIds.join(',')]);

  useEffect(() => { load(); }, [load]);

  const mutate = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (e: any) {
      toast.error(e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const move = (to: string) =>
    mutate(async () => {
      await releaseApi.transition(release._id, to);
      await load();
    }, `Moved to ${to}`);

  const approve = () =>
    mutate(async () => {
      await releaseApi.approve(release._id, 'approved');
      await load();
    }, 'Approval recorded');

  const waive = (check: string) => {
    const reason = window.prompt(
      'Why is this check being waived? The reason is recorded against the release.');
    if (!reason?.trim()) return;
    mutate(async () => {
      await releaseApi.waive(release._id, check, reason.trim());
      await load();
    }, 'Waiver recorded');
  };

  const fork = () =>
    mutate(async () => {
      await releaseApi.standalone(release._id, {
        workItemIds: forkPicked,
        plannedDate: forkDate || undefined,
      });
      setForking(false);
      setForkPicked([]);
      setForkDate('');
    }, 'Off-cycle release created — it requires approval before it ships');

  return (
    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700 space-y-4">
      {/* Scope */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Scope
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setAdding(!adding)}
              className="flex items-center gap-1 text-xs text-accent hover:underline"
            >
              <Plus size={12} /> Add items
            </button>
            {release.releaseType !== 'standalone' && items.length > 0 && (
              <button
                onClick={() => setForking(!forking)}
                className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
              >
                Fork off-cycle
              </button>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="text-xs text-gray-400">
            Nothing assigned. An empty release closes itself at its planned date
            and produces no notes.
          </p>
        ) : (
          <ul className="space-y-1">
            {items.map((w) => (
              <li key={w._id} className="flex items-center gap-2 text-sm">
                <span className="text-[10px] font-mono text-gray-400 w-14">
                  {w.displayId}
                </span>
                <span className="text-[10px] uppercase text-gray-400 w-16">
                  {w.workItemType}
                </span>
                <span className="flex-1 text-gray-800 dark:text-gray-200 truncate">
                  {w.title}
                </span>
                <span className="text-[10px] text-gray-400">{w.status}</span>
                {forking && (
                  <input
                    type="checkbox"
                    checked={forkPicked.includes(w._id)}
                    onChange={(e) => setForkPicked(
                      e.target.checked
                        ? [...forkPicked, w._id]
                        : forkPicked.filter((x) => x !== w._id),
                    )}
                    className="rounded border-gray-300 text-amber-600"
                  />
                )}
                {!forking && (
                  <button
                    disabled={busy}
                    onClick={() => mutate(
                      () => releaseApi.removeItems(release._id, [w._id]),
                      'Removed from release',
                    )}
                    className="p-0.5 text-gray-400 hover:text-red-500 disabled:opacity-40"
                    title="Remove from release"
                  >
                    <X size={12} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {forking && (
          <div className="mt-2 p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 space-y-2">
            <p className="text-[11px] text-amber-800 dark:text-amber-300">
              An off-cycle release stays bound to this sprint but may ship on any
              date, and always requires approval.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={forkDate}
                onChange={(e) => setForkDate(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              <button
                onClick={fork}
                disabled={busy || forkPicked.length === 0}
                className="px-2 py-1 text-xs rounded bg-amber-600 text-white disabled:opacity-40"
              >
                <Check size={12} className="inline mr-1" />
                Fork {forkPicked.length || ''}
              </button>
              <button
                onClick={() => { setForking(false); setForkPicked([]); }}
                className="text-xs text-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {adding && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
            {candidates.length === 0 ? (
              <p className="text-xs text-gray-400 p-2">
                No unassigned features, bugs or security items in this project.
              </p>
            ) : candidates.map((w) => (
              <button
                key={w._id}
                disabled={busy}
                onClick={() => mutate(
                  () => releaseApi.addItems(release._id, [w._id]),
                  'Added to release',
                )}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
              >
                <span className="text-[10px] font-mono text-gray-400 w-14">{w.displayId}</span>
                <span className="text-[10px] uppercase text-gray-400 w-16">{w.workItemType}</span>
                <span className="flex-1 truncate text-gray-800 dark:text-gray-200">{w.title}</span>
                <Plus size={12} className="text-accent" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Readiness + lifecycle (F9) */}
      {readiness && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <ClipboardCheck size={12} />
              Readiness
              {readiness.ready ? (
                <span className="normal-case text-[10px] text-green-600 dark:text-green-400">
                  ready to ship
                </span>
              ) : (
                <span className="normal-case text-[10px] text-amber-600 dark:text-amber-400">
                  {readiness.blockingCount} blocking
                </span>
              )}
            </h3>
            {lifecycle?.approvalRequired && (
              <span className={clsx(
                'flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full',
                lifecycle.approved
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
              )}>
                <ShieldCheck size={10} />
                {lifecycle.approved ? 'approved' : 'approval required'}
              </span>
            )}
          </div>

          <ul className="space-y-1">
            {readiness.checks.map((c) => (
              <li key={c.check} className="text-sm">
                <div className="flex items-start gap-2">
                  {c.passed ? (
                    <Check size={13} className="text-green-500 shrink-0 mt-0.5" />
                  ) : c.waived ? (
                    <ShieldCheck size={13} className="text-blue-500 shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                  )}
                  <span className={clsx(
                    'flex-1',
                    c.passed || c.waived
                      ? 'text-gray-600 dark:text-gray-400'
                      : 'text-gray-800 dark:text-gray-200',
                  )}>
                    {c.label}
                    <span className="text-xs text-gray-400"> — {c.summary}</span>
                    {c.waived && c.waiver && (
                      <span className="block text-[11px] text-blue-600 dark:text-blue-400">
                        waived: {c.waiver.reason}
                      </span>
                    )}
                  </span>
                  {!c.passed && !c.waived && c.waivable && (
                    <button
                      onClick={() => waive(c.check)}
                      className="text-[11px] text-gray-400 hover:text-accent shrink-0"
                      title="Waive with a recorded reason"
                    >
                      waive
                    </button>
                  )}
                  {!c.passed && !c.waivable && (
                    <span className="text-[10px] text-gray-400 shrink-0"
                          title="This check cannot be waived">
                      mandatory
                    </span>
                  )}
                </div>
                {!c.passed && c.blocking.length > 0 && (
                  <ul className="ml-6 mt-0.5 space-y-0.5">
                    {c.blocking.map((b) => (
                      <li key={b.id} className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-mono text-[10px] mr-1">{b.displayId}</span>
                        {b.title}
                        <span className="text-gray-400"> ({b.status})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          {lifecycle?.workflow && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {lifecycle.workflow.name}: <strong>{lifecycle.current}</strong> →
              </span>
              {lifecycle.allowed.length === 0 && (
                <span className="text-xs text-gray-400">no moves from here</span>
              )}
              {lifecycle.allowed.map((target) => (
                <button
                  key={target}
                  disabled={busy}
                  onClick={() => move(target)}
                  className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-40"
                >
                  {target}
                </button>
              ))}
              {lifecycle.approvalRequired && !lifecycle.approved && (
                <button
                  disabled={busy}
                  onClick={approve}
                  className="ml-auto px-2 py-1 text-xs rounded bg-accent text-white disabled:opacity-40"
                >
                  Approve
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {/* Changes / PR rollup */}
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
          <GitPullRequest size={12} />
          Changes
          {changes && changes.unmergedCount > 0 && (
            <span className="text-[10px] normal-case text-amber-600 dark:text-amber-400">
              {changes.unmergedCount} unmerged
            </span>
          )}
        </h3>
        {!changes || changes.changes.length === 0 ? (
          <p className="text-xs text-gray-400">
            No pull requests linked yet.
            {changes && changes.itemsWithoutLinks.length > 0 && (
              <> Unlinked: {changes.itemsWithoutLinks.join(', ')}</>
            )}
          </p>
        ) : (
          <ul className="space-y-1">
            {changes.changes.map((c) => (
              <li key={c.linkId} className="flex items-center gap-2 text-sm">
                {c.state !== 'merged' && (
                  <AlertTriangle size={12} className="text-amber-500 shrink-0" />
                )}
                <a
                  href={c.url} target="_blank" rel="noreferrer"
                  className="text-accent hover:underline font-mono text-xs"
                >
                  #{c.number}
                </a>
                <span className="flex-1 truncate text-gray-700 dark:text-gray-300">
                  {c.title}
                </span>
                <span className="text-[10px] text-gray-400">
                  {c.workItems.map((w: any) => w.displayId).join(', ')}
                </span>
                <span className="text-[10px] uppercase text-gray-400 w-14 text-right">
                  {c.state}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
